import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PenLine, Loader2, Upload, Save } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailSignatureCard() {
  const [data, setData] = useState(null);
  const [rep, setRep] = useState({ title: "", phone: "" });
  const [org, setOrg] = useState({ disclaimer: "", site: "" });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const logoRef = useRef();
  const stripRef = useRef();

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/sales/signature`);
    setData(r.data);
    setRep({ title: r.data.rep.title || "", phone: r.data.rep.phone || "" });
    setOrg({ disclaimer: r.data.org.disclaimer || "", site: r.data.org.site || "" });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sales/signature`, { title: rep.title, phone: rep.phone, disclaimer: org.disclaimer, site: org.site });
      toast.success("Signature saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the signature"); }
    finally { setSaving(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/sales/signature/asset?kind=${kind}`, fd);
      await axios.put(`${API}/sales/signature`, { [kind === "logo" ? "logo_url" : "strip_url"]: `${API}/sales/signature/asset/${kind}?v=${Date.now()}` });
      toast.success(`${kind === "logo" ? "Logo" : "Product strip"} uploaded`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <Card className="border-[#E7E2D6]" data-testid="email-signature-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <PenLine className="w-4 h-4 text-[#8A6D00]" />
          <h3 className="heading text-[15px] text-[#1A1A1A]">Email signature</h3>
          <span className="text-xs text-[#8A857A]">— auto-added to every prospect &amp; nurture email you send</span>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Your title</label>
                <Input value={rep.title} onChange={(e) => setRep({ ...rep, title: e.target.value })} placeholder="e.g. Account Executive" data-testid="sig-title" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Your phone</label>
                <Input value={rep.phone} onChange={(e) => setRep({ ...rep, phone: e.target.value })} placeholder="1300 100 365" data-testid="sig-phone" />
              </div>
            </div>
            <div className="text-[11px] text-[#8A857A]">Sends from your address <b>{data.rep.email || "your equipr.software email"}</b> as <b>{data.rep.name}</b>.</div>
            <div>
              <label className="text-xs text-[#59554D]">Website</label>
              <Input value={org.site} onChange={(e) => setOrg({ ...org, site: e.target.value })} placeholder="www.equiprsoftware.com" data-testid="sig-site" />
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Disclaimer <span className="text-[#8A857A]">(shared)</span></label>
              <Textarea rows={3} value={org.disclaimer} onChange={(e) => setOrg({ ...org, disclaimer: e.target.value })} className="text-[12px]" data-testid="sig-disclaimer" />
            </div>
            <div className="flex flex-wrap gap-2">
              <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => upload("logo", e.target.files?.[0])} data-testid="sig-logo-file" />
              <input ref={stripRef} type="file" accept="image/*" hidden onChange={(e) => upload("strip", e.target.files?.[0])} data-testid="sig-strip-file" />
              <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={busy === "logo"} data-testid="sig-upload-logo">
                {busy === "logo" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Logo
              </Button>
              <Button variant="outline" size="sm" onClick={() => stripRef.current?.click()} disabled={busy === "strip"} data-testid="sig-upload-strip">
                {busy === "strip" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Product strip
              </Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] ml-auto" data-testid="sig-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
              </Button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.09em] text-[#8A857A] mb-1.5">Live preview</div>
            <div className="rounded-lg border border-[#E7E2D6] bg-white p-4 max-h-[360px] overflow-y-auto" data-testid="sig-preview"
              dangerouslySetInnerHTML={{ __html: data.html }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
